import {
  algorithms,
  calculation_mode,
  IStation,
  IVehiclesCapacities,
  IVersion,
  sizes_enum,
  universe_mode,
  vehicle_types,
  Version,
} from '@helpers/types.ts';
import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useLogin } from '@hooks/use-login.ts';
import { GENERIC_REDUCER_EXTENDED } from '@helpers/constants.tsx';
import { Statistic } from '@helpers/statistics.ts';
import * as XLSX from 'xlsx';
import { useDownload } from '@hooks/use-download.ts';
import { useHttp } from '@hooks/use-http';

interface IConfigurePlan {
  plan_id: string;
  plan_name: string;
  prefix: string;
  sizes: Array<sizes_enum>;
  vehicles: Partial<IVehiclesCapacities>;
  routeTime: number;
  touched: boolean;
  metrics: Statistic | undefined;
}

interface IConfigureReducer {
  [key: string]: Partial<IConfigurePlan>;
}

type Dispatcher = Dispatch<IConfigureReducer>;

export type IFormReducer = [IConfigureReducer, Dispatcher];

const defaultPlan: IConfigurePlan = {
  plan_id: '',
  plan_name: '',
  prefix: '',
  sizes: [
    sizes_enum.xs,
    sizes_enum.sm,
    sizes_enum.md,
    sizes_enum.lg,
    sizes_enum.xl,
  ],
  vehicles: {
    [vehicle_types.motorcycle]: 0,
    [vehicle_types.auto]: 0,
    [vehicle_types.small_van]: 0,
    [vehicle_types.large_van]: 0,
  },
  routeTime: 9,
  touched: false,
  metrics: undefined,
};
export const sizes_enums = [
  sizes_enum.xs,
  sizes_enum.sm,
  sizes_enum.md,
  sizes_enum.lg,
  sizes_enum.xl,
];
export const useConfigurePlanStack = (station: IStation | null) => {
  const http = useHttp();
  const { email, name } = useLogin();
  const { downloadXlsx } = useDownload();
  const [preloadPlan, setPreloadPlan] = useState<boolean>(false);
  const [universe, _setUniverseMode] = useState<Array<universe_mode>>([
    universe_mode.station,
    //universe_mode.station_return,
    universe_mode.linehaul,
    //universe_mode.linehaul_return,
  ]);
  const [versions, setVersions] = useState<Array<Version>>([]);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [expanded, setExpanded] = useState<string>('');
  // @ts-ignore
  const [configurations, setConfigurations]: IFormReducer = useReducer(
    GENERIC_REDUCER_EXTENDED,
    {},
  );

  const onChangeVersion = useCallback((value: Array<Version>) => {
    setVersions(value);
  }, []);

  const onChangeFile = useCallback((file: File) => {
    setFile(file);
  }, []);

  const versions_dic = useMemo(() => {
    const dic: any = {};
    versions.forEach((v) => {
      dic[v._id] = v;
    });
    return dic;
  }, [versions]);

  const isValidVehicles = useCallback((vehicles: any) => {
    return (
      (vehicles[vehicle_types.motorcycle] || 0) +
        (vehicles[vehicle_types.auto] || 0) +
        (vehicles[vehicle_types.small_van] || 0) +
        (vehicles[vehicle_types.large_van] || 0) >
      0
    );
  }, []);

  const isValidForm = useMemo(() => {
    let valid = true;
    const items = Object.keys(configurations);
    if (items.length === 0) {
      setError('Debe ingresar al menos un plan');
      return false;
    }
    items.forEach((key) => {
      const config = configurations[key];
      if (!config || !config.sizes || !config.vehicles) return;

      if (!config.plan_name) {
        valid = false;
      }
      if (!config.prefix) {
        valid = false;
      }
      if (config.sizes.length === 0) {
        valid = false;
      }
      if (!isValidVehicles(config.vehicles)) {
        setError(`Debe ingresar al menos un vehículo en cada linea`);
        valid = false;
        return;
      }
      if (
        config.sizes.includes(sizes_enum.xl) &&
        (config.vehicles.large_van || 0) <= 0
      ) {
        setError(`Si maneja paquetes XL debe agregar al menos un vehiculo LG`);
        valid = false;
        return;
      }
    });
    return valid;
  }, [configurations, isValidVehicles]);

  const isValidForCalculate = useMemo(() => {
    let valid = true;
    const items = Object.keys(configurations);
    if (!file) {
      setError('Debe ingresar un archivo');
      return false;
    }
    if (items.length === 0) {
      setError('Debe ingresar al menos un plan');
      return false;
    }
    items.forEach((key) => {
      if (configurations[key].touched) {
        valid = false;
      }
    });
    return valid;
  }, [file, configurations]);

  const onCngPlanName = useCallback((key: string, plan_name: string) => {
    setConfigurations({ [key]: { touched: true, plan_name } });
  }, []);

  const onCngPrefix = useCallback((key: string, prefix: string) => {
    prefix = prefix.toLocaleUpperCase();
    setConfigurations({ [key]: { touched: true, prefix } });
  }, []);

  const onCngSizes = useCallback(
    (key: string, size: sizes_enum) => {
      let sizes = configurations[key].sizes || [];
      if (sizes.includes(size)) {
        sizes = sizes.filter((s: sizes_enum) => s !== size);
      } else {
        sizes.push(size);
      }
      setConfigurations({ [key]: { touched: true, sizes } });
    },
    [configurations],
  );

  const onChgVehicles = useCallback(
    (key: string, vehicle: vehicle_types, value: number) => {
      setConfigurations({
        [key]: { touched: true, vehicles: { [vehicle]: value } },
      });
    },
    [],
  );

  const onChgRouteTime = useCallback((key: string, value: number) => {
    setConfigurations({ [key]: { touched: true, routeTime: value } });
  }, []);

  useEffect(() => {
    if (versions.length === 0) {
      setConfigurations({});
      return;
    }
    const versionsId = versions.map((v) => v._id);
    const newVersions = versions.filter((v) => !configurations[v._id]);
    const removedVersions = Object.keys(configurations).filter(
      (v) => !versionsId.includes(v),
    );
    const existingVersions = versions.filter((v) => configurations[v._id]);

    const newConfigurations: any = existingVersions.reduce(
      (acc, version) => ({
        ...acc,
        [version._id]: configurations[version._id],
      }),
      {},
    );

    newVersions.forEach((version) => {
      newConfigurations[version._id] = defaultPlan;
    });

    removedVersions.forEach((version) => {
      newConfigurations[version] = undefined;
    });

    setConfigurations(newConfigurations);
  }, [versions, configurations]);

  const onVerifySinglePlan = useCallback(
    async (
      key: string,
      config: IConfigurePlan,
      version: IVersion,
      station: IStation,
      universe: Array<universe_mode>,
      file: File,
    ) => {
      try {
        setError(null);
        setPreloadPlan(true);
        setLoadingMessage(
          'Espere un momento, estamos verificando el universo de ordenes',
        );

        const m = await http.lastmile.getRoutingVerification(
          config.plan_id,
          config.plan_name,
          config.prefix,
          station.station,
          station.country,
          calculation_mode.manual,
          universe,
          config.sizes,
          version,
          config.vehicles,
          file,
          0,
          config.routeTime,
          0,
          algorithms.k_means,
        );
        setConfigurations({
          [key]: { plan_id: m.plan_id, touched: false, metrics: m },
        });
      } catch (error: any) {
        setError(error.response?.data?.message || error.message);
      } finally {
        setLoadingMessage('');
        setPreloadPlan(false);
      }
    },
    [http],
  );

  const onStartSimulation = useCallback(
    async (plan_id: string, email: string, name: string) => {
      await http.lastmile.startKSimulation(plan_id, `${email} | ${name}`);
    },
    [http],
  );

  const onTryValidatePlans = useCallback(async () => {
    try {
      const keys = Object.keys(configurations);
      setPreloadPlan(true);
      for (const key of keys) {
        const config = configurations[key];
        if (config.touched) {
          await onVerifySinglePlan(
            key,
            configurations[key] as IConfigurePlan,
            versions_dic[key],
            station!,
            universe,
            file!,
          );
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setPreloadPlan(false);
    }
  }, [configurations, versions_dic, station, universe, file, onVerifySinglePlan]);

  const onTrySimulate = useCallback(async () => {
    try {
      setLoadingMessage(
        `Simulación iniciada. Por favor, no recargue el sitio hastá finalizar; Este proceso puede tardar varios minutos`,
      );
      const plans = Object.keys(configurations).map((key) =>
        onStartSimulation(configurations[key].plan_id!, email, name),
      );

      await Promise.all(plans).catch((e) => {
        console.log(e);
      });
      window.location.href = '/';
    } catch (error: any) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoadingMessage('');
    }
  }, [configurations, email, name, onStartSimulation]);

  const onToggleExpand = useCallback(
    (key: string) => {
      if (expanded === key) {
        setExpanded('');
      } else {
        setExpanded(key);
      }
    },
    [expanded],
  );

  const onDownloadPlan = useCallback(async () => {
    if (!file) return;
    const workbooks = XLSX.read(await file.arrayBuffer());
    const SheetNames = workbooks.SheetNames;
    const jsonExcel = XLSX.utils.sheet_to_json(workbooks.Sheets[SheetNames[0]]);
    const allTrackings = jsonExcel.map((j: any) => j['#Guia99Minutos']);
    const keys = Object.keys(configurations);

    const usedTrackings: any[] = [];
    await Promise.all(
      keys.map(async (key) => {
        const unexpected = configurations[key].metrics?.out_zones;
        allTrackings // @ts-ignore
          .filter((t: any) => !unexpected?.includes(t))
          .forEach((t: any) => {
            usedTrackings.push(t);
          });
      }),
    );

    const unusedTrackings = allTrackings.filter(
      (t: any) => !usedTrackings.includes(t),
    );

    downloadXlsx(
      unusedTrackings.map((x: any) => ({
        tracking_id: x,
      })),
      'ordenes-sin-zona.xlsx',
    );
  }, [file, configurations, downloadXlsx]);

  return {
    preloadPlan,
    universe,
    versions,
    file,
    error,
    loadingMessage,
    onChangeVersion,
    onChangeFile,
    configurations,
    versions_dic,
    isValidForm,
    onCngPlanName,
    onCngPrefix,
    onCngSizes,
    onChgVehicles,
    onChgRouteTime,
    onTryValidatePlans,
    onTrySimulate,
    isValidForCalculate,
    expanded,
    onToggleExpand,
    onDownloadPlan,
  };
};
